import { DownOutlined } from '@ant-design/icons';
import { Button, Col } from 'antd';
import { CSSProperties, FC, ReactNode, useCallback } from 'react';
import { ControllerProps } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { ProgramFormItem } from './ProgramFormItem';
import { ProgramSection } from './ui/ProgramSection';
import { ProgramSectionGroup } from './ui/ProgramSectionGroup';
import { TransparentInput } from './ui/TransparentInput';

interface BaseProps {
  addonAfter?: ReactNode;
  addonBefore?: ReactNode;
  children?: ReactNode;
  isDropdown?: boolean;
  isOpen?: boolean;
  onToggleDropdown?: () => void;
  style?: CSSProperties;
  sectionTitle?: string;
}

interface TitleInputProps {
  control: ControllerProps<any>['control'];
  name: ControllerProps['name'];
  placeholder: string;
  disabled?: boolean;
}

interface EditableTitleProps extends BaseProps {
  isEditableTitle: true;
  editableTitleProps: TitleInputProps;
  title?: string;
}

interface NonEditableTitleProps extends BaseProps {
  isEditableTitle?: false;
  editableTitleProps?: TitleInputProps;
  title: string;
}

type ProgramSectionItemProps = EditableTitleProps | NonEditableTitleProps;

export const ProgramSectionItem: FC<ProgramSectionItemProps> = ({
  addonAfter,
  addonBefore,
  children,
  isDropdown,
  isEditableTitle,
  editableTitleProps,
  isOpen,
  onToggleDropdown,
  title,
  style,
  sectionTitle,
}) => {
  const renderEditableTitle = useCallback(
    (editableTitleProps: TitleInputProps): JSX.Element => {
      const {
        control,
        name,
        placeholder,
        disabled = false,
      } = editableTitleProps;

      return (
        <ProgramFormItem
          control={control}
          name={name}
          renderItem={({ field }) => (
            <TransparentInput
              {...field}
              disabled={disabled}
              placeholder={placeholder}
            />
          )}
        />
      );
    },
    [editableTitleProps]
  );

  return (
    <ProgramSection title={sectionTitle} style={style}>
      <ProgramSectionGroup isHeader={(isDropdown && isOpen) || !isDropdown}>
        <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
          {addonBefore}

          {isEditableTitle && editableTitleProps ? (
            renderEditableTitle(editableTitleProps)
          ) : (
            <Col flex={1}>{title}</Col>
          )}

          {addonAfter}

          {isDropdown && (
            <Button
              icon={<Arrow isOpen={isOpen} />}
              onClick={onToggleDropdown}
              style={{
                border: 'none',
                width: '22px',
                height: '22px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '8px',
              }}
            />
          )}
        </div>
      </ProgramSectionGroup>

      {isDropdown ? (
        <DropdownContent isOpen={isOpen}>{children}</DropdownContent>
      ) : (
        children
      )}
    </ProgramSection>
  );
};

const Arrow = styled(DownOutlined)<{
  isOpen: boolean | undefined;
}>`
  transition: transform 0.3s ease;
  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;

const DropdownContent = styled.div<{ isOpen: boolean | undefined }>`
  overflow: hidden;
  transition: height 0.3s ease, opacity 0.3s ease;
  height: ${({ isOpen }) => (isOpen ? 'auto' : '0')};
  opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
`;
